import { useMemo, useState } from 'react';
import { CLOUD_REFERENCES } from '../cloud.js';
import { recommendForMachine } from '../recommend.js';
import { type RouteDecision, planRouting } from '../router.js';
import { suitabilityByTask, worthRunningLocal } from '../score.js';
import type { MachineProfile } from '../types.js';
import { Bar, VerdictPill } from '../ui.js';

interface RoutingTabProps {
  machine: MachineProfile;
  mode: 'beginner' | 'pro';
}

const BUDGETS: Array<{ id: 'free' | 'low' | 'standard' | 'premium'; label: string; subtitle: string }> = [
  { id: 'free', label: 'Free', subtitle: 'Local first wherever possible.' },
  { id: 'low', label: 'Low budget', subtitle: 'Local default, cheap cloud only when needed.' },
  { id: 'standard', label: 'Standard', subtitle: 'Sonnet 4.6 cloud default, escalate where it matters.' },
  { id: 'premium', label: 'Premium', subtitle: 'Opus 4.7 for reasoning + debugging.' },
];

export function RoutingTab({ machine, mode }: RoutingTabProps) {
  const [budget, setBudget] = useState<'free' | 'low' | 'standard' | 'premium'>('standard');
  const [preferLocal, setPreferLocal] = useState(false);
  const [hours, setHours] = useState(4);

  const plan = useMemo(() => planRouting(machine, { budget, preferLocal }), [machine, budget, preferLocal]);
  const topPick = useMemo(() => recommendForMachine(machine, 'best-coding', 1)[0], [machine]);
  const cloudRef =
    CLOUD_REFERENCES.find((c) =>
      budget === 'free' || budget === 'low' ? c.id === 'gpt-4o-mini' : c.id === 'claude-sonnet-4-6',
    ) ?? CLOUD_REFERENCES[0];
  const worth = topPick ? worthRunningLocal(topPick.model, topPick.fit, cloudRef, hours) : null;
  const suit = topPick ? suitabilityByTask(topPick.model, topPick.fit) : [];

  const totalCloudIfAll = plan.taskRoutes.reduce((s, r) => s + r.monthlyCloudCost, 0);
  const cloudIfHybrid = plan.taskRoutes
    .filter((r) => r.decision !== 'local')
    .reduce((s, r) => s + r.monthlyCloudCost, 0);

  return (
    <div className="lab-routing">
      <header className="lab-routing-head">
        <div>
          <span className="section-kicker">Router intelligence</span>
          <h3>Local-vs-cloud, per task</h3>
          <p>
            Vibetime can route each task type to the best model. The plan below uses your detected machine,
            your monthly budget, and the live fit estimates.
          </p>
        </div>
        <div className="lab-routing-controls">
          <fieldset className="lab-budget-pills">
            <legend>Budget</legend>
            {BUDGETS.map((b) => (
              <button
                key={b.id}
                type="button"
                className={`lab-budget-chip ${budget === b.id ? 'lab-budget-active' : ''}`}
                onClick={() => setBudget(b.id)}
                title={b.subtitle}
              >
                {b.label}
              </button>
            ))}
          </fieldset>
          <label className="lab-routing-toggle">
            <input type="checkbox" checked={preferLocal} onChange={(e) => setPreferLocal(e.target.checked)} />
            <span>Prefer local when close in quality</span>
          </label>
          <label className="lab-routing-hours">
            <span>Hours/day of coding</span>
            <input
              type="range"
              min={1}
              max={12}
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
            />
            <strong>{hours}h</strong>
          </label>
        </div>
      </header>

      {worth && (
        <article className={`lab-worth lab-worth-${worth.band}`}>
          <header>
            <span className="section-kicker">Worth running local?</span>
            <strong>{worth.headline}</strong>
            <span className="lab-worth-score">{worth.score}/100</span>
          </header>
          <Bar
            value={worth.score}
            max={100}
            tone={worth.band === 'yes' ? 'mint' : worth.band === 'maybe' ? 'gold' : 'coral'}
          />
          <ul>
            {worth.reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </article>
      )}

      <section className="lab-routing-summary">
        <h4>Plan summary</h4>
        <p>{plan.summary}</p>
        <div className="lab-routing-cost">
          <article>
            <span>If you use cloud for everything</span>
            <strong>${totalCloudIfAll.toFixed(0)}/mo</strong>
          </article>
          <article>
            <span>With this hybrid plan</span>
            <strong>${cloudIfHybrid.toFixed(0)}/mo</strong>
          </article>
          <article>
            <span>You save</span>
            <strong>${Math.max(0, totalCloudIfAll - cloudIfHybrid).toFixed(0)}/mo</strong>
          </article>
        </div>
      </section>

      <section className="lab-routing-table">
        <h4>Per-task routing</h4>
        <table className="lab-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Decision</th>
              <th>Local pick</th>
              <th>Cloud pick</th>
              <th>Quality (local / cloud)</th>
              <th>Speed (tok/s)</th>
              <th>$/mo</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {plan.taskRoutes.map((r) => (
              <tr key={r.task}>
                <td>
                  <strong>{r.task}</strong>
                </td>
                <td>
                  <DecisionBadge decision={r.decision} />
                </td>
                <td>{r.localModel?.displayName ?? '—'}</td>
                <td>{r.cloudModel?.displayName ?? '—'}</td>
                <td>
                  {r.localQuality.toFixed(0)} / {r.cloudQuality.toFixed(0)}
                </td>
                <td>
                  {r.localTps} / {r.cloudTps}
                </td>
                <td>${r.monthlyCloudCost.toFixed(2)}</td>
                <td className="lab-routing-rationale">{r.rationale}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {mode === 'beginner' && (
        <aside className="lab-routing-explainer">
          <h4>What is this?</h4>
          <p>
            The routing plan is what Vibetime would use if you let it auto-pick a model per task. Local
            handles the hot loops and tool calls, cloud handles the cases where the quality gap is real.
            Toggle "prefer local" to bias even more toward local picks.
          </p>
        </aside>
      )}

      {topPick && suit.length > 0 && (
        <section className="lab-routing-suitability">
          <h4>Local suitability by task — {topPick.model.displayName}</h4>
          <ul>
            {suit.map((s) => (
              <li key={s.task} className={s.suitable ? 'lab-suit-yes' : 'lab-suit-no'}>
                <strong>{s.task}</strong>
                <VerdictPill verdict={s.suitable ? 'good' : 'chat-only'} />
                <span>{s.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function DecisionBadge({ decision }: { decision: RouteDecision }) {
  return <span className={`lab-decision lab-decision-${decision}`}>{decision}</span>;
}
